import { useState, useCallback } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletAuth, WalletType } from '../../auth/useWalletAuth'
import { useAuth } from '../../auth/AuthContext'
import { Spinner } from '../common/Spinner'
import { ErrorBanner } from '../common/ErrorBanner'

const WALLETS: { type: WalletType; name: string; icon: string; installUrl: string }[] = [
  { type: 'phantom',  name: 'Phantom',  icon: '🔮', installUrl: 'https://phantom.app' },
  { type: 'solflare', name: 'Solflare', icon: '🌟', installUrl: 'https://solflare.com' },
]

export function ConnectWalletButton({ label = 'Connect Wallet' }: { label?: string }) {
  const { isAuthenticated, portfolioId, logout } = useAuth()
  const { connect, state, error, available } = useWalletAuth()
  const { disconnect: adapterDisconnect } = useWallet()
  const [open, setOpen] = useState(false)

  const busy = state === 'connecting' || state === 'signing' || state === 'verifying'

  /**
   * Full logout — clears SolQuant session AND tears down every wallet session
   * that might hold a stale public key. Critical for the "switch wallet" flow:
   * without this, `@solana/wallet-adapter-react` may keep the old wallet's
   * publicKey after logout, which leads to a session/adapter mismatch when
   * the next wallet logs in.
   */
  const fullLogout = useCallback(async () => {
    try { await adapterDisconnect() } catch { /* noop — adapter may already be idle */ }
    try { await (window as any).solana?.disconnect?.()   } catch { /* noop */ }
    try { await (window as any).solflare?.disconnect?.() } catch { /* noop */ }
    logout()
  }, [adapterDisconnect, logout])

  if (isAuthenticated && portfolioId) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-xs text-ink-muted font-mono">
          {portfolioId.slice(0, 6)}…{portfolioId.slice(-4)}
        </span>
        <button
          onClick={fullLogout}
          className="text-xs text-gray-500 hover:text-accent-red transition-colors"
        >
          Disconnect
        </button>
      </div>
    )
  }

  const busyLabel =
    state === 'connecting' ? 'Connecting…' :
    state === 'signing'    ? 'Sign message…' :
    state === 'verifying'  ? 'Verifying…' : null

  function handleSelect(type: WalletType) {
    setOpen(false)
    connect(type)
  }

  return (
    <div className="relative flex flex-col items-end gap-2">
      <button
        onClick={() => !busy && setOpen(o => !o)}
        disabled={busy}
        className="lab-btn disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy && <Spinner size="sm" />}
        {busyLabel ?? label}
      </button>

      {open && !busy && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-52 rounded-xl border border-fringe-strong bg-surface-card shadow-xl overflow-hidden">
            <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-fringe">
              Select wallet
            </p>
            {WALLETS.map(({ type, name, icon, installUrl }) => {
              const isAvailable = available[type]
              return (
                <button
                  key={type}
                  onClick={() => isAvailable ? handleSelect(type) : window.open(installUrl, '_blank')}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-surface-hover transition-colors"
                >
                  <span className="text-lg leading-none">{icon}</span>
                  <span className="flex-1 text-ink font-medium">{name}</span>
                  {isAvailable
                    ? <span className="text-xs text-accent-green">Detected</span>
                    : <span className="text-xs text-gray-400">Install</span>
                  }
                </button>
              )
            })}
          </div>
        </>
      )}

      {error && <ErrorBanner message={error} />}
    </div>
  )
}
